import { RAW_OEM_GENERATION_SOURCE_REGISTER } from '../../data/generationOemSourceRegister';
import { hashCanonicalJson } from '../reportGeneration/reportContractHash';
import { GENERATION_EQUIPMENT_CATALOG } from './generationEquipmentCatalog';
import {
  OEM_GENERATION_SOURCE_REGISTER_SCHEMA_VERSION,
  type OemFootprintAssessment,
  type OemGenerationCatalogCoverage,
  type OemGenerationPerformanceCase,
  type OemGenerationProduct,
  type OemGenerationSourceRegister,
  type OemPhysicalReference,
} from '../../types/oemGenerationCatalog';

const MAX_CATALOG_STRING_LENGTH = 4_000;

function deepFreeze<T>(value: T): T {
  if (value && typeof value === 'object' && !Object.isFrozen(value)) {
    Object.freeze(value);
    Object.values(value as Record<string, unknown>).forEach(deepFreeze);
  }
  return value;
}

/** Immutable, research-only OEM product and source register. */
export const OEM_GENERATION_SOURCE_REGISTER: OemGenerationSourceRegister = deepFreeze(
  RAW_OEM_GENERATION_SOURCE_REGISTER,
);

export const OEM_GENERATION_SOURCE_REGISTER_DIGEST = hashCanonicalJson(
  OEM_GENERATION_SOURCE_REGISTER,
);

export function findOemGenerationProduct(id: string): OemGenerationProduct | undefined {
  return OEM_GENERATION_SOURCE_REGISTER.products.find(product => product.id === id);
}

export function oemGenerationProductSourceIds(
  product: OemGenerationProduct,
): readonly string[] {
  const ids = new Set<string>(product.identitySourceIds);
  product.performanceCases.forEach(item => item.sourceIds.forEach(id => ids.add(id)));
  product.partLoadPoints.forEach(item => item.sourceIds.forEach(id => ids.add(id)));
  product.heatRecoveryCases.forEach(item => item.sourceIds.forEach(id => ids.add(id)));
  product.physicalReferences.forEach(item => item.sourceIds.forEach(id => ids.add(id)));
  product.clearances.forEach(item => item.sourceIds.forEach(id => ids.add(id)));
  product.electrical.sourceIds.forEach(id => ids.add(id));
  return [...ids].sort();
}

export function oemGenerationProductRecordDigest(
  product: OemGenerationProduct,
  register: OemGenerationSourceRegister = OEM_GENERATION_SOURCE_REGISTER,
): string {
  const sourceIds = new Set(oemGenerationProductSourceIds(product));
  return hashCanonicalJson({
    product,
    sources: register.sources.filter(source => sourceIds.has(source.id)),
  });
}

export function oemElectricalMachineRecordDigest(
  machineId: string,
  register: OemGenerationSourceRegister = OEM_GENERATION_SOURCE_REGISTER,
): string | null {
  const machine = register.electricalMachines.find(candidate => candidate.id === machineId);
  if (!machine) return null;
  const sourceIds = new Set<string>(machine.identitySourceIds);
  machine.ratingCases.forEach(item => item.sourceIds.forEach(id => sourceIds.add(id)));
  machine.studyCases.forEach(item => item.sourceIds.forEach(id => sourceIds.add(id)));
  machine.physicalReferences.forEach(item => item.sourceIds.forEach(id => sourceIds.add(id)));
  return hashCanonicalJson({
    machine,
    sources: register.sources.filter(source => sourceIds.has(source.id)),
  });
}

export function preferredOemPerformanceCase(
  product: OemGenerationProduct,
): OemGenerationPerformanceCase {
  const performance = product.performanceCases.find(
    candidate => candidate.id === product.preferredPerformanceCaseId,
  );
  if (!performance) {
    throw new RangeError(
      `OEM generation product ${product.id} has no preferred performance case ${product.preferredPerformanceCaseId}.`,
    );
  }
  return performance;
}

function footprintArea(reference: OemPhysicalReference): number {
  return reference.lengthM * reference.widthM;
}

function exactBeforeFamily(a: OemPhysicalReference, b: OemPhysicalReference): number {
  const rank = { 'exact-product': 0, 'product-family': 1, 'project-example': 2 } as const;
  return rank[a.configurationScope] - rank[b.configurationScope];
}

/**
 * Classify the strongest published spatial evidence without promoting a skid,
 * transport outline, or typical marketing layout into an installed test fit.
 */
export function assessOemProductFootprint(
  product: OemGenerationProduct,
): OemFootprintAssessment {
  const installed = product.physicalReferences
    .filter(reference => reference.scope === 'installed-operating-envelope')
    .sort(exactBeforeFamily)[0];
  if (installed) {
    const exact = installed.configurationScope === 'exact-product';
    const maintenance = installed.maintenanceClearanceIncluded === true;
    const fire = installed.fireAccessIncluded === true;
    const usable = exact && maintenance && fire;
    return {
      authority: usable
        ? 'project-ready-installed-envelope'
        : 'preliminary-plant-layout',
      physicalReferenceId: installed.id,
      lengthM: installed.lengthM,
      widthM: installed.widthM,
      areaM2: footprintArea(installed),
      maintenanceClearanceIncluded: maintenance,
      fireAccessIncluded: fire,
      usableAsProjectInstalledReservation: usable,
      warnings: usable
        ? []
        : [
          'The installed envelope is not project-ready because exact-product scope, maintenance clearance, and fire access are not all confirmed.',
        ],
    };
  }

  const typicalLayout = product.physicalReferences
    .filter(reference => reference.scope === 'typical-plant-layout')
    .sort(exactBeforeFamily)[0];
  if (typicalLayout) {
    return {
      authority: 'preliminary-plant-layout',
      physicalReferenceId: typicalLayout.id,
      lengthM: typicalLayout.lengthM,
      widthM: typicalLayout.widthM,
      areaM2: footprintArea(typicalLayout),
      maintenanceClearanceIncluded: typicalLayout.maintenanceClearanceIncluded === true,
      fireAccessIncluded: typicalLayout.fireAccessIncluded === true,
      usableAsProjectInstalledReservation: false,
      warnings: [
        'Typical OEM layout is screening evidence only; obtain the selected configuration plot plan and confirm maintenance and fire access before applying it to the project test fit.',
      ],
    };
  }

  for (const clearance of product.clearances) {
    const reference = product.physicalReferences.find(
      candidate => candidate.id === clearance.physicalReferenceId,
    );
    if (!reference) continue;
    const lengthM = reference.lengthM + clearance.frontM + clearance.rearM;
    const widthM = reference.widthM + clearance.leftM + clearance.rightM;
    return {
      authority: 'preliminary-clearance-envelope',
      physicalReferenceId: reference.id,
      lengthM,
      widthM,
      areaM2: lengthM * widthM,
      maintenanceClearanceIncluded: true,
      fireAccessIncluded: false,
      usableAsProjectInstalledReservation: false,
      warnings: [
        'Clearance-bounded envelope is derived from published equipment dimensions and minimum service clearances; it does not establish fire access or balance-of-plant reservations.',
      ],
    };
  }

  const reference = product.physicalReferences
    .filter(candidate => candidate.scope !== 'engine-only')
    .sort(exactBeforeFamily)[0] ?? product.physicalReferences[0];
  if (reference) {
    return {
      authority: 'equipment-reference-only',
      physicalReferenceId: reference.id,
      lengthM: reference.lengthM,
      widthM: reference.widthM,
      areaM2: footprintArea(reference),
      maintenanceClearanceIncluded: reference.maintenanceClearanceIncluded === true,
      fireAccessIncluded: reference.fireAccessIncluded === true,
      usableAsProjectInstalledReservation: false,
      warnings: [
        `${reference.scope.replaceAll('-', ' ')} dimensions are not an installed plant reservation.`,
      ],
    };
  }

  return {
    authority: 'missing',
    physicalReferenceId: null,
    lengthM: null,
    widthM: null,
    areaM2: null,
    maintenanceClearanceIncluded: false,
    fireAccessIncluded: false,
    usableAsProjectInstalledReservation: false,
    warnings: ['No bounded OEM physical reference is available.'],
  };
}

export function oemGenerationCatalogCoverage(): OemGenerationCatalogCoverage {
  const products = OEM_GENERATION_SOURCE_REGISTER.products;
  const electricalMachines = OEM_GENERATION_SOURCE_REGISTER.electricalMachines;
  const footprints = products.map(assessOemProductFootprint);
  const manufacturers = new Set(products.map(product => product.manufacturer));
  return {
    productCount: products.length,
    manufacturerCount: manufacturers.size,
    gasTurbineCount: products.filter(product => product.technology.includes('turbine')).length,
    reciprocatingCount: products.filter(product => !product.technology.includes('turbine')).length,
    withPartLoadData: products.filter(product => product.partLoadPoints.length > 0).length,
    withPackageDimensions: products.filter(product => product.physicalReferences.length > 0).length,
    withPreliminaryOrBetterFootprint: footprints.filter(
      footprint => footprint.authority === 'project-ready-installed-envelope'
        || footprint.authority === 'preliminary-plant-layout'
        || footprint.authority === 'preliminary-clearance-envelope',
    ).length,
    withProjectReadyInstalledFootprint: footprints.filter(
      footprint => footprint.authority === 'project-ready-installed-envelope',
    ).length,
    linkedSimulationEnvelopeCount: products.filter(product => product.simulationCatalogId).length,
    electricalMachineCount: electricalMachines.length,
    electricalMachineManufacturerCount: new Set(
      electricalMachines.map(machine => machine.manufacturer),
    ).size,
  };
}

function finiteInRange(value: unknown, minimum: number, maximum: number): boolean {
  return typeof value === 'number'
    && Number.isFinite(value)
    && value >= minimum
    && value <= maximum;
}

function validDate(value: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const parsed = new Date(Date.UTC(year, month - 1, day));
  return parsed.getUTCFullYear() === year
    && parsed.getUTCMonth() === month - 1
    && parsed.getUTCDate() === day;
}

function optionalFiniteInRange(
  value: number | null,
  minimum: number,
  maximum: number,
): boolean {
  return value == null || finiteInRange(value, minimum, maximum);
}

function sourceReferencesExist(
  sourceIds: readonly string[],
  knownSourceIds: ReadonlySet<string>,
  context: string,
  errors: string[],
): void {
  if (sourceIds.length === 0) errors.push(`${context} requires at least one source id`);
  for (const sourceId of sourceIds) {
    if (!knownSourceIds.has(sourceId)) errors.push(`${context} references unknown source ${sourceId}`);
  }
}

/** Runtime and CI guard for hand-curated OEM records. */
export function validateOemGenerationSourceRegister(
  register: OemGenerationSourceRegister = OEM_GENERATION_SOURCE_REGISTER,
): readonly string[] {
  const errors: string[] = [];
  if (register.schemaVersion !== OEM_GENERATION_SOURCE_REGISTER_SCHEMA_VERSION) {
    errors.push('unsupported OEM generation source-register schema version');
  }
  if (!validDate(register.compiledAt)) errors.push('compiledAt must be an ISO calendar date');

  const sourceIds = new Set<string>();
  for (const source of register.sources) {
    if (!source.id || source.id.length > MAX_CATALOG_STRING_LENGTH) {
      errors.push('source id must be a non-empty bounded string');
    }
    if (sourceIds.has(source.id)) errors.push(`duplicate source id ${source.id}`);
    sourceIds.add(source.id);
    if (!source.publisher.trim() || !source.title.trim()) {
      errors.push(`source ${source.id} requires publisher and title`);
    }
    if (!source.url.startsWith('https://')) errors.push(`source ${source.id} must use HTTPS`);
    if (!validDate(source.retrievedAt)) errors.push(`source ${source.id} has invalid retrievedAt`);
    if (source.publishedAt && !validDate(source.publishedAt)) {
      errors.push(`source ${source.id} has invalid publishedAt`);
    }
  }

  const productIds = new Set<string>();
  const simulationIds = new Set(GENERATION_EQUIPMENT_CATALOG.map(entry => entry.catalogId));
  for (const product of register.products) {
    if (!product.id || product.id.length > MAX_CATALOG_STRING_LENGTH) {
      errors.push('product id must be a non-empty bounded string');
    }
    if (productIds.has(product.id)) errors.push(`duplicate product id ${product.id}`);
    productIds.add(product.id);
    sourceReferencesExist(product.identitySourceIds, sourceIds, `${product.id} identity`, errors);
    if (product.simulationCatalogId && !simulationIds.has(product.simulationCatalogId)) {
      errors.push(`${product.id} links unknown simulation catalog id ${product.simulationCatalogId}`);
    }
    if (product.performanceCases.length === 0) {
      errors.push(`${product.id} requires at least one performance case`);
    }

    const performanceIds = new Set<string>();
    for (const performance of product.performanceCases) {
      if (!performance.id.trim()) errors.push(`${product.id} has an empty performance case id`);
      if (performanceIds.has(performance.id)) {
        errors.push(`${product.id} has duplicate performance case ${performance.id}`);
      }
      performanceIds.add(performance.id);
      if (!finiteInRange(performance.electricalOutputKw, 0.001, 2_000_000)) {
        errors.push(`${product.id}/${performance.id} output must be finite and positive`);
      }
      if (performance.powerFactor != null && !finiteInRange(performance.powerFactor, 0.01, 1)) {
        errors.push(`${product.id}/${performance.id} power factor must be in (0, 1]`);
      }
      if (performance.electricalEfficiencyPct != null
        && !finiteInRange(performance.electricalEfficiencyPct, 0.01, 100)) {
        errors.push(`${product.id}/${performance.id} efficiency must be in (0, 100]`);
      }
      if (performance.heatRate
        && !finiteInRange(performance.heatRate.valueKjPerKwh, 1_000, 100_000)) {
        errors.push(`${product.id}/${performance.id} heat rate is outside engineering bounds`);
      }
      if (!optionalFiniteInRange(performance.fuelInputKw, 0.001, 10_000_000)
        || !optionalFiniteInRange(performance.exhaustMassFlowKgS, 0, 100_000)
        || !optionalFiniteInRange(performance.exhaustTemperatureC, -273.15, 3_000)
        || !optionalFiniteInRange(performance.steamProductionKgPerHour, 0, 100_000_000)
        || !optionalFiniteInRange(performance.minimumLoadPct, 0, 100)
        || !optionalFiniteInRange(performance.startTimeMinutes, 0, 100_000)
        || !optionalFiniteInRange(performance.rampRateMwPerMinute, 0, 100_000)) {
        errors.push(`${product.id}/${performance.id} has an out-of-range performance value`);
      }
      if (new Set(performance.frequencyHz).size !== performance.frequencyHz.length) {
        errors.push(`${product.id}/${performance.id} repeats a frequency`);
      }
      if (!performance.referenceConditions.trim()) {
        errors.push(`${product.id}/${performance.id} requires reference conditions`);
      }
      if (performance.heatRate && performance.electricalEfficiencyPct != null) {
        const efficiencyFromHeatRate = 360_000 / performance.heatRate.valueKjPerKwh;
        if (Math.abs(efficiencyFromHeatRate - performance.electricalEfficiencyPct) > 2) {
          errors.push(`${product.id}/${performance.id} heat rate and efficiency do not reconcile`);
        }
      }
      if (performance.fuelInputKw != null && performance.electricalEfficiencyPct != null) {
        const efficiencyFromFuel = performance.electricalOutputKw / performance.fuelInputKw * 100;
        if (Math.abs(efficiencyFromFuel - performance.electricalEfficiencyPct) > 2) {
          errors.push(`${product.id}/${performance.id} fuel input and efficiency do not reconcile`);
        }
      }
      sourceReferencesExist(
        performance.sourceIds,
        sourceIds,
        `${product.id}/${performance.id}`,
        errors,
      );
    }
    if (!performanceIds.has(product.preferredPerformanceCaseId)) {
      errors.push(`${product.id} preferred performance case does not exist`);
    }

    const partLoadKeys = new Set<string>();
    for (const point of product.partLoadPoints) {
      if (!performanceIds.has(point.performanceCaseId)) {
        errors.push(`${product.id} part-load point references unknown performance case`);
      }
      if (!finiteInRange(point.loadPct, 0, 100)) {
        errors.push(`${product.id} part-load point must be in [0, 100]`);
      }
      const partLoadKey = `${point.performanceCaseId}/${point.loadPct}`;
      if (partLoadKeys.has(partLoadKey)) {
        errors.push(`${product.id} repeats part-load point ${partLoadKey}`);
      }
      partLoadKeys.add(partLoadKey);
      if (!optionalFiniteInRange(point.electricalOutputKw, 0, 2_000_000)
        || !optionalFiniteInRange(point.electricalEfficiencyPct, 0.01, 100)
        || !optionalFiniteInRange(point.fuelInputKw, 0, 10_000_000)
        || (point.heatRate != null
          && !finiteInRange(point.heatRate.valueKjPerKwh, 1_000, 100_000))) {
        errors.push(`${product.id} has an out-of-range part-load value at ${partLoadKey}`);
      }
      sourceReferencesExist(point.sourceIds, sourceIds, `${product.id} part-load point`, errors);
    }
    for (const heat of product.heatRecoveryCases) {
      if (!performanceIds.has(heat.performanceCaseId)) {
        errors.push(`${product.id} heat-recovery case references unknown performance case`);
      }
      const heatValues = [
        heat.jacketWaterKw,
        heat.firstStageChargeAirKw,
        heat.secondStageChargeAirKw,
        heat.lubeOilKw,
        heat.exhaustKw,
        heat.totalThermalOutputKw,
      ];
      if (heatValues.every(value => value == null)) {
        errors.push(`${product.id} heat-recovery case requires a published heat value`);
      }
      if (heatValues.some(value => !optionalFiniteInRange(value, 0, 10_000_000))) {
        errors.push(`${product.id} heat-recovery case has an out-of-range heat value`);
      }
      sourceReferencesExist(heat.sourceIds, sourceIds, `${product.id} heat recovery`, errors);
    }
    sourceReferencesExist(product.electrical.sourceIds, sourceIds, `${product.id} electrical`, errors);
    if (!optionalFiniteInRange(product.electrical.ratedKva, 0.001, 10_000_000)
      || !optionalFiniteInRange(product.electrical.ratedPowerFactor, 0.01, 1)
      || !optionalFiniteInRange(product.electrical.motorStartingKva, 0, 100_000_000)
      || !optionalFiniteInRange(product.electrical.rotorInertiaKgM2, 0, 1_000_000_000)
      || product.electrical.nominalVoltagesKv.some(voltage => !finiteInRange(voltage, 0.001, 1_200))) {
      errors.push(`${product.id} has an out-of-range electrical value`);
    }

    const physicalIds = new Set<string>();
    for (const physical of product.physicalReferences) {
      if (physicalIds.has(physical.id)) errors.push(`${product.id} has duplicate physical id ${physical.id}`);
      physicalIds.add(physical.id);
      if (!finiteInRange(physical.lengthM, 0.01, 20_000)
        || !finiteInRange(physical.widthM, 0.01, 20_000)
        || (physical.heightM != null && !finiteInRange(physical.heightM, 0.01, 2_000))
        || (physical.massTonnes != null && !finiteInRange(physical.massTonnes, 0.001, 1_000_000))
        || !Number.isInteger(physical.quantity)
        || !finiteInRange(physical.quantity, 1, 100_000)) {
        errors.push(`${product.id}/${physical.id} has invalid physical dimensions or mass`);
      }
      if (physical.includes.length === 0 || !physical.limitation.trim()) {
        errors.push(`${product.id}/${physical.id} requires included systems and a limitation`);
      }
      sourceReferencesExist(physical.sourceIds, sourceIds, `${product.id}/${physical.id}`, errors);
    }
    for (const clearance of product.clearances) {
      if (!physicalIds.has(clearance.physicalReferenceId)) {
        errors.push(`${product.id} clearance references unknown physical record`);
      }
      const values = [clearance.frontM, clearance.rearM, clearance.leftM, clearance.rightM];
      if (values.some(value => !finiteInRange(value, 0, 1_000))
        || (clearance.topM != null && !finiteInRange(clearance.topM, 0, 1_000))) {
        errors.push(`${product.id} has invalid clearance dimensions`);
      }
      if (!clearance.limitation.trim()) errors.push(`${product.id} clearance requires a limitation`);
      sourceReferencesExist(clearance.sourceIds, sourceIds, `${product.id} clearance`, errors);
    }

    if (product.dataGaps.length === 0 || product.dataGaps.some(gap => !gap.trim())) {
      errors.push(`${product.id} must retain explicit data gaps`);
    }
  }

  const electricalMachineIds = new Set<string>();
  for (const machine of register.electricalMachines) {
    if (!machine.id || machine.id.length > MAX_CATALOG_STRING_LENGTH) {
      errors.push('electrical-machine id must be a non-empty bounded string');
    }
    if (electricalMachineIds.has(machine.id)) {
      errors.push(`duplicate electrical-machine id ${machine.id}`);
    }
    electricalMachineIds.add(machine.id);
    sourceReferencesExist(machine.identitySourceIds, sourceIds, `${machine.id} identity`, errors);
    if (machine.ratingCases.length === 0) {
      errors.push(`${machine.id} requires at least one exact rating case`);
    }

    const ratingIds = new Set<string>();
    for (const rating of machine.ratingCases) {
      if (!rating.id.trim() || ratingIds.has(rating.id)) {
        errors.push(`${machine.id} has an empty or duplicate rating case ${rating.id}`);
      }
      ratingIds.add(rating.id);
      if (!finiteInRange(rating.ratedKva, 0.001, 10_000_000)
        || !finiteInRange(rating.ratedVoltageKv, 0.001, 1_200)
        || !optionalFiniteInRange(rating.ratedPowerFactor, 0.01, 1)
        || !Number.isInteger(rating.poles)
        || !finiteInRange(rating.poles, 2, 100)
        || !finiteInRange(rating.ratedSpeedRpm, 1, 100_000)
        || !optionalFiniteInRange(rating.efficiencyPct, 0.01, 100)) {
        errors.push(`${machine.id}/${rating.id} has an out-of-range rating value`);
      }
      const synchronousSpeedRpm = 120 * rating.frequencyHz / rating.poles;
      if (Math.abs(synchronousSpeedRpm - rating.ratedSpeedRpm) > 1) {
        errors.push(`${machine.id}/${rating.id} speed does not match frequency and pole count`);
      }
      sourceReferencesExist(rating.sourceIds, sourceIds, `${machine.id}/${rating.id}`, errors);
    }
    if (!ratingIds.has(machine.preferredRatingCaseId)) {
      errors.push(`${machine.id} preferred rating case does not exist`);
    }

    const studyRatingIds = new Set<string>();
    for (const study of machine.studyCases) {
      if (!ratingIds.has(study.ratingCaseId)) {
        errors.push(`${machine.id} study case references unknown rating ${study.ratingCaseId}`);
      }
      if (studyRatingIds.has(study.ratingCaseId)) {
        errors.push(`${machine.id} repeats study data for rating ${study.ratingCaseId}`);
      }
      studyRatingIds.add(study.ratingCaseId);
      const scalarValues = [
        study.statorResistancePu,
        study.shortCircuitRatio,
        study.rotorInertiaKgM2,
        study.inertiaConstantSeconds,
      ];
      if (scalarValues.some(value => !optionalFiniteInRange(value, 0, 1_000_000_000))) {
        errors.push(`${machine.id}/${study.ratingCaseId} has an out-of-range study value`);
      }
      if (study.reactances) {
        const reactanceValues = [
          study.reactances.xdPu,
          study.reactances.xPrimeDPu,
          study.reactances.xDoublePrimeDPu,
          study.reactances.xqPu,
          study.reactances.xDoublePrimeQPu,
          study.reactances.negativeSequencePu,
          study.reactances.zeroSequencePu,
          study.reactances.leakagePu,
        ];
        if (!finiteInRange(study.reactances.baseMva, 0.000_001, 10_000)
          || reactanceValues.some(value => !optionalFiniteInRange(value, 0, 100))) {
          errors.push(`${machine.id}/${study.ratingCaseId} has invalid reactance data`);
        }
      }
      if (study.timeConstants) {
        const timeValues = Object.values(study.timeConstants);
        if (timeValues.some(value => !optionalFiniteInRange(value, 0, 100_000))) {
          errors.push(`${machine.id}/${study.ratingCaseId} has invalid time constants`);
        }
      }
      sourceReferencesExist(
        study.sourceIds,
        sourceIds,
        `${machine.id}/${study.ratingCaseId} study data`,
        errors,
      );
    }

    const physicalIds = new Set<string>();
    for (const physical of machine.physicalReferences) {
      if (physicalIds.has(physical.id)) {
        errors.push(`${machine.id} has duplicate physical id ${physical.id}`);
      }
      physicalIds.add(physical.id);
      if (!finiteInRange(physical.lengthM, 0.01, 20_000)
        || !finiteInRange(physical.widthM, 0.01, 20_000)
        || (physical.heightM != null && !finiteInRange(physical.heightM, 0.01, 2_000))
        || (physical.massTonnes != null && !finiteInRange(physical.massTonnes, 0.001, 1_000_000))) {
        errors.push(`${machine.id}/${physical.id} has invalid physical dimensions or mass`);
      }
      sourceReferencesExist(physical.sourceIds, sourceIds, `${machine.id}/${physical.id}`, errors);
    }
    if (machine.dataGaps.length === 0 || machine.dataGaps.some(gap => !gap.trim())) {
      errors.push(`${machine.id} must retain explicit data gaps`);
    }
  }
  return errors;
}
